import type { AiCapabilityPage } from "../schema.js";
import { ALL_ROLES, LIVE, WRITE_FLAGS, SLOT_COLLECTION_AUTOFILL, SLOT_GROUP } from "./_shared.js";

/**
 * `DUE-CLAIM-WITHDRAW` (@UC0198) — rút lại LỜI BÁO đã chuyển (khi chưa được đối chiếu), T2.
 * Executor = `ReportPaymentService.cancelReport` — cùng cửa với `DELETE /v1/member-dues/:id/confirmations`.
 *
 * 🔴🔴 **MẶT SAU của `PAY-REPORT`, và nó thừa hưởng đúng cái bẫy đã bắt ở @UC0197.** Bản `draft` viết
 * *"Khoản này quay về trạng thái chưa đóng"*. Câu đó **SAI với suất đã đóng dở**: `_cancelInTx` trả trạng thái
 * bằng `nextDueStatusAfterPayment(amountMinor, paidAmountMinor)`, nên suất 500k đã đóng 200k rồi rút lời khai
 * thì về `partially_paid` — và **200k kia vẫn nguyên trong sổ cái**. Nói "chưa đóng" ở đó là bảo người dùng
 * rằng tiền họ đã đóng biến mất.
 *
 * Và đây KHÔNG phải ca hiếm — chính service ghi: *"huỷ-rồi-khai-lại là ĐƯỜNG CHÍNH THỨC để sửa một lời khai gõ
 * nhầm số (QĐ người dùng 01/09/2026, thay cho việc cho nhiều lời khai chờ cùng lúc), nên đường này nay chạy
 * thường xuyên — sai ở đây không còn là ca hiếm."*
 *
 * ⇒ hệ quả nằm trong **GIÁ TRỊ của fact `trangThaiSau`**, không trong câu tĩnh — đúng khuôn `ketQua` của
 * @UC0197 và `duGhi` của @UC0200. `preview` ĐOÁN bằng chính `nextDueStatusAfterPayment`; `execute` BÁO LẠI từ
 * `res.newDueStatus` mà service vừa ghi ⇒ hai NGUỒN ĐỘC LẬP, lệch nhau là `FACTS_MISMATCH` ⇒ rollback.
 *
 * 🔴 **§3.7 — trang này chỉ chạm trạng thái (2) «khai đã trả».** `cancelReport` đòi
 * `due.status === awaiting_confirmation`, ngược lại **409**. Nghĩa là:
 *  · lời khai đã được XÁC NHẬN (đã thành bút toán) **không rút được ở đây** — sửa khi đó là **REVERSAL** của
 *    thủ quỹ (§3.3: `txType=REVERSAL` + `reversesTxId`, và partial unique ⇒ **mỗi tx gốc chỉ đảo MỘT lần**);
 *  · ở nhóm bật `self_payment_auto_confirm` — hoặc khi người khai là O/A/T — lời khai **vào sổ ngay** (@UC0197),
 *    nên nó **không bao giờ dừng ở (2)** và cửa này không có gì để rút. Câu từ chối phải nói THẬT cả hai khả
 *    năng ("chưa báo" HOẶC "đã được xác nhận rồi"), chứ không được nói cụt "không tìm thấy".
 *
 * 🔴 **Ai được rút = ĐÚNG người được báo**, không suy từ actor đang bấm (`CLAUDE.md` §3.6). `cancelReport` gác
 * bằng CHÍNH vị-từ ghế của `reportPayment` (`primaryUserId` hoặc `userIsLinkedToSeat`) và tự ghi lý do: *"Ai báo
 * được thì cũng phải huỷ được: mở một chiều mà khoá chiều kia là để lại một lời khai về tiền mà người khai không
 * rút lại nổi."* Vì vậy `roles` của trang này **bằng đúng** `roles` của `PAY-REPORT` — có ca khoá quan hệ đó.
 *
 * 🔴 **KHÔNG khai `executorMayNoop`**: cửa này không REPLAY lặng. `_cancelInTx` khoá row rồi re-check trạng thái
 * TRONG tx ⇒ lượt thứ hai thấy đã đổi ⇒ **409 fail-loud**, không trả nguyên kết quả cũ.
 */
export const DUE_CLAIM_WITHDRAW = {
  id: "DUE-CLAIM-WITHDRAW",
  uc: "@UC0198",
  tier: "T2",
  status: LIVE,
  // Bằng ĐÚNG vai của PAY-REPORT: ai báo được thì phải rút được (xem docblock).
  roles: ALL_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    SLOT_GROUP,
    { ...SLOT_COLLECTION_AUTOFILL, id: "khoan", required: true, ask: "Rút lại lời báo cho khoản nào?" },
    /*
     * 🔴 UX-WITHDRAW-SEAT (@UC0198 · nghiệm thu UX-H4 15/10) — Ô CHỌN BÉ, cùng khuôn `be` của `PAY-REPORT` (@UC0373). Phụ huynh hai bé cùng
     * một đợt, cả hai đều đã báo ⇒ trước đây `SEAT_AMBIGUOUS` ở bước cuối («chưa làm giúp được trong trò chuyện», ngõ cụt). Phạm vi ĐÓNG
     * `represented` (suất người hỏi đại diện — tên bé nhà khác không phân giải được); tuỳ chọn, KHÔNG `autofill`: đúng một lời báo rút được ⇒
     * binding tự chọn hàng duy nhất (không hỏi); ≥2 ⇒ Người làm hỏi `ask` kèm nút từng bé (`slotCandidates` = CHÍNH phép cấp quyền của cửa
     * rút ∩ phạm vi resolver). Ô vào prompt ⇒ đổi bản ghi eval của mọi ca có trang này trong top-K.
     */
    { id: "be", type: "member", required: false, memberScope: "represented", ask: "Bạn rút lời báo của bé nào? (tên bé / suất bạn đại diện)" },
  ],
  llm: {
    description: "Rút lại một lời báo đã chuyển tiền mà người dùng vừa gửi và thủ quỹ chưa đối chiếu.",
    examples: [
      "rút lại lời báo đã chuyển tiền",
      "rut lai loi bao da chuyen",
      "tôi báo nhầm, chưa chuyển đâu",
      "toi bao nham chua chuyen",
      "huỷ báo đã đóng tiền tháng 9",
      "huy bao da dong",
      "rút lại",
      "chưa chuyển mà lỡ bấm đã chuyển",
      "bỏ lời báo chuyển khoản vừa rồi",
      "hủy khai báo",
      "rút báo",
      "huỷ lời báo",
      // ——— 0105: thêm cho đủ 40 ———
      "rut bao",
      "huy loi bao",
      "rut lai",
      "huy khai bao",
      "chua chuyen ma lo bam da chuyen",
      "bo loi bao chuyen khoan vua roi",
      "tôi lỡ báo đã chuyển, rút lại giúp",
      "toi lo bao da chuyen rut lai giup",
      "báo nhầm số tiền, huỷ lời báo đi",
      "bao nham so tien huy loi bao di",
      "rút lại lời khai đã đóng của tôi",
      "rut lai loi khai da dong cua toi",
      "tôi bấm đã chuyển nhầm khoản",
      "toi bam da chuyen nham khoan",
      "huỷ cái báo đã chuyển hồi nãy",
      "huy cai bao da chuyen hoi nay",
      "gõ sai số khi báo đã đóng, rút lại nhé",
      "go sai so khi bao da dong rut lai nhe",
      "thu hồi lời báo chuyển khoản",
      "thu hoi loi bao chuyen khoan",
      "xoá lời báo đã đóng của mình",
      "xoa loi bao da dong cua minh",
      "rút lại báo đã chuyển thì làm sao?",
      "rut lai bao da chuyen thi lam sao",
      "mình chưa chuyển thật, huỷ báo giúp",
      "minh chua chuyen that huy bao giup",
      "bỏ cái đã báo đóng quỹ lớp",
      "bo cai da bao dong quy lop",
      "huỷ báo chuyển khoản tháng này",
      "huy bao chuyen khoan thang nay",
    ],
    // 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI (việc XEM) không thuộc trang ghi này; mỗi câu là ví dụ của một trang đọc ⇒ eval đo được.
    questionsNotHere: ["còn khoản nào của tôi chưa đóng không"],
  },
  executor: "reportPayment.cancelReport",
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: rút lại lời khai của CHÍNH MÌNH. */
  endpoints: [
    "DELETE /member-dues/{memberDueId}/confirmations",
  ],
  facts: ["tenDot", "tenSuat", "soDaKhai", "trangThaiSau", "khoanDaRut"],
  templates: {
    // Thứ tự chỗ trống = thứ tự DÒNG PHIẾU: nhóm → số đang rút → khoản → suất (tuỳ chọn) → kết cục.
    // 🔴 KHÔNG có chữ "chưa đóng"/"quay về trạng thái" nào ở đây — kết cục do SỐ ĐÃ ĐÓNG quyết, và nó ở `{trangThaiSau}`.
    // 🔴 UX-WITHDRAW-SEAT (@UC0198) — `{tenSuat}` trong ĐOẠN TUỲ CHỌN: phụ huynh nhiều bé phải ĐỌC tên bé trên phiếu trước khi bấm (AI-INV-4 —
    // hai lời báo cùng đợt cùng số chỉ phân biệt được bằng tên bé); suất không tên riêng (nhóm thường) ⇒ fact rỗng ⇒ đoạn + dòng phiếu biến mất.
    confirm: "Ở nhóm {nhom}, rút lại lời báo đã chuyển {soDaKhai} cho khoản “{tenDot}”[[ — suất của {tenSuat}]]. {trangThaiSau}",
    // 🔴 UX-POB-WORDING (@UC0198 · GUIDE-UX2 V-2 mục 5) — XONG nêu suất như phiếu đã nêu (phụ huynh hai bé biết lời báo của bé nào vừa rút). `done` không
    // được đoạn tuỳ chọn (validator — lượt `answer` có thể qua làm mượt) và chỗ trống không được rỗng ⇒ fact `khoanDaRut` LUÔN có chữ: «“<đợt>”» hoặc
    // «“<đợt>” — suất của <bé>» (binding, cùng nguồn `tenSuat`).
    done: "Đã rút lời báo {soDaKhai} cho {khoanDaRut}. {trangThaiSau} Khi chuyển xong, bạn báo lại là được.",
  },
  /** P5.3 — nhãn NGƯỜI ĐỌC cho từng dòng phiếu T2; thiếu một nhãn thì `renderConfirm` ném. */
  labels: {
    nhom: "Nhóm",
    tenDot: "Khoản",
    tenSuat: "Của suất",
    soDaKhai: "Lời báo đang rút",
    trangThaiSau: "Sau khi rút",
  },
  journey: { outcomes: { done: { next: ["PAY-REPORT", "READ-PEOPLE-MINE"] } } },
  help: { title: "Rút lại báo đã nộp tiền", tryNow: "rút lại lời báo đã chuyển tiền" },
} satisfies AiCapabilityPage;
